import { useMutation } from '@apollo/client/react';
import { notify } from '@components/ui/molecules/Toast';
import { VerifyEmailDocument } from '@graphql/types';
import { useCallback } from 'react';

import type { VerifyEmailInput, VerifyEmailResponse } from '../types';

export const useVerifyEmail = () => {
  const [verifyEmailMutation, { loading: verifyEmailLoading }] = useMutation(VerifyEmailDocument);
  /**
   * Executes the verifyEmail mutation and returns the payload or throws.
   */
  const doVerifyEmail = useCallback(
    async (input: VerifyEmailInput): Promise<VerifyEmailResponse> => {
      const response = await verifyEmailMutation({
        variables: { input: input },
      });
      const verifyEmailRes = response.data?.verifyEmail;

      if (!verifyEmailRes) {
        // No response at all — network/server error, nothing useful to return
        throw new Error('Email verification failed');
      }

      if (verifyEmailRes.errors?.length) {
        // Return errors to the caller so they can show a friendly message;
        // do NOT throw here — the `if (errors)` branch in the Provider handles this.
        notify.error({ title: verifyEmailRes.errors[0]?.message });
      } else {
        notify.success({ title: 'Email verification successful' });
      }

      return verifyEmailRes as VerifyEmailResponse;
    },
    [verifyEmailMutation]
  );

  return { doVerifyEmail, verifyEmailLoading };
};
